/* eslint no-underscore-dangle: 0, vars-on-top: 0, no-var: 0, prefer-destructuring: 0 */

import ReactGA from 'react-ga';
import {
  School,
  SchoolClass,
  formatRealmAccess,
  formatSchoolsLicencesForIntercom,
  formatStudentsPerClass,
  hasAnyCurrentLicenses,
} from './intercomUserDataFormatting';
import { trackIntercomEvent, updateIntercomUser } from './intercom';

window._hsq = window._hsq || [];
var _hsq = window._hsq;

export default function track(id: string, data?: Record<string, any>) {
  _hsq.push(['trackEvent', { ...data, id }]);

  trackIntercomEvent(id, data);
}

export const trackPageView = (pathname: string) => {
  _hsq.push(['setPath', pathname]);
  _hsq.push(['trackPageView']);

  ReactGA.set({ page: pathname }); // Update the user's current page
  ReactGA.pageview(pathname); // Record a pageview for the given page
};

export const trackUser = ({
  id,
  userHash,
  email,
  firstName,
  lastName,
  realm,
  userType,
  schools,
  classes,
  hardwarePreferences,
  ...rest
}: {
  id: string;
  userHash: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  realm: string | null;
  userType: string | null;
  schools: School[] | null;
  classes: SchoolClass[] | null;
  [key: string]: any;
}) => {
  // Disable tracking while running tests, in order to prevent test runs from creating contacts in Intercom
  if ((window as any).NODE_ENV === 'test') {
    return;
  }

  const numberOfSchools = schools?.length ?? 0;

  const numberOfClasses =
    classes?.filter((c) => !c.teacherTrainingType).length ?? 0;
  const schoolLicenses = schools
    ? formatSchoolsLicencesForIntercom(schools)
    : '⚠️ No schools, therefore no licenses';

  // Check if the user currently has any valid school licenses.
  // This information is used to tick the checklist item for teachers to activate their school license.
  // Once it has been ticked, we want it to remain ticked forever.
  // Therefore we either send `true` or don't send it (`undefined` excludes it from the Intercom user data).
  const hasHadLicense: true | undefined = hasAnyCurrentLicenses(schools)
    ? true
    : undefined;

  const studentsPerClass = classes ? formatStudentsPerClass(classes) : '';
  const realmAccess = schools
    ? formatRealmAccess(schools, id)
    : '⚠️ No schools, therefore no access to realms';

  const userEmailDomain = email?.split('@')[1];
  const intercomBotEnabledEmailDomains =
    process.env.VENDOR_INTERCOM_BOT_ENABLED_EMAIL_DOMAINS?.split(',') || [];

  const intercomBotEnabled =
    userType === 'TEACHER' &&
    userEmailDomain &&
    intercomBotEnabledEmailDomains.includes(userEmailDomain);

  const hardwarePreferencesString = hardwarePreferences
    ? hardwarePreferences.join(', ')
    : 'N/A';

  if (userType && userType.toUpperCase() !== 'STUDENT') {
    _hsq.push([
      'identify',
      {
        email,
        id,
        firstName,
        lastName,
        further_realm: realm,
        userType,
        numberOfSchools,
        numberOfClasses,
        schoolLicenses,
        hasHadLicense,
        studentsPerClass,
        realmAccess,
        intercomBotEnabled,
        hardwarePreferences: hardwarePreferencesString,
        ...rest, // usertype, nomarketing, membershiptype
      },
    ]);
  }

  if (userType) {
    const intercomUserData: any = {
      user_id: id,
      user_hash: userHash,
      email,
      name: `${firstName} ${lastName}`,
      realm,
      userType,
      numberOfSchools,
      numberOfClasses,
      schoolLicenses,
      studentsPerClass,
      realmAccess,
      intercomBotEnabled,
      hardwarePreferences: hardwarePreferencesString,
      ...rest, // userType, noMarketing, membershipType
    };

    // Only include hasHadLicense if it's true
    if (hasHadLicense) {
      intercomUserData.hasHadLicense = hasHadLicense;
    }

    updateIntercomUser(intercomUserData);
  }

  ReactGA.set({
    userId: id,
  });
};
